import React, { useState, useEffect } from 'react'
import DisplaysToolbarContentMethods, { IDisplaysToolbarContentProps } from './displays__toolbarContent.local.methods'
import Dotdotdot from 'react-dotdotdot'
import Icon from 'blocks.simple/icon/icon'
import Rectangle from 'blocks.simple/rectangle/rectangle'
import DisplaysScreenShots from '../__screenShots/displays__screenShots'
import NotifyBadge from 'blocks.simple/notifyBadge/notifyBadge'
import Checkbox from 'blocks.simple/checkbox/checkbox'
import Tag from 'blocks.simple/tag/tag'
import RangeVolume from 'blocks.simple/range/_volume/range_volume'
import DropMenu from 'blocks.simple/dropMenu/dropMenu'
import Button from 'blocks.simple/button/button'
import ModalAccept from 'blocks.simple/modal/_accept/modal_accept'
import enhanceWithClickOutside from 'react-click-outside'
import toolbarMethods from '../__toolbar/displays__toolbar.methods'
import helpers from 'core/helpers'
import displaysHelpers from 'core/helpers/_digitalSignages'
import { routes } from 'features/routes'
import { api } from 'core/api/ConnectionManager'
import { cn } from 'ethcss'
import styles from './displays__toolbarContent.jcss'
import grid from 'blocks.simple/grid/grid.jcss'
import { text as textStyles } from 'theme'
import translate from 'core/translate'
import item from 'blocks.simple/item/item.jcss'
import { Tooltip } from 'atoms/Tooltip'
import { Typography } from 'atoms/Typography'
import { getURLSearchParamsString } from 'features/routes/utils'
import { changeLocation } from 'features/routes'
import { isExist } from 'core/utils/coreUtil'
import { connect } from 'react-redux'
import {
    disableIdentification,
    displaysActions,
    enableIdentification,
    IdentificationDevice,
    IDisplaysModal,
} from '../displays.state'
import { ISelectedInfo } from '../__info/displays__info.local.methods'
import DotTooltip from '../../../blocks.simple/dotTooltip/dotTooltip'

const getDigitalSignageAndGroupIds = (list: Pick<IdentificationDevice, 'id' | 'type'>[]) => {
    const id: number[] = []
    const groupId: number[] = []

    list.forEach((item) => {
        item.type === 'group' ? groupId.push(item.id) : id.push(item.id)
    })

    return { id, groupId }
}

const configureScheduleLocations = (selectedInfo: { id: number; name: string }, type = 'digitalSignages') =>
    JSON.stringify({
        [type]: [
            {
                id: selectedInfo.id,
                name: selectedInfo.name,
            },
        ],
    })

const getClientVersion = (selectedInfo: Pick<ISelectedInfo, 'newerClientVersion'> | undefined, menuItem: string) => {
    const newerClientVersion = selectedInfo?.newerClientVersion

    if (newerClientVersion && menuItem === 'information') {
        return (
            <div className={cn(grid.ml_micro, grid.row, grid.normalCenter, grid.fullHeight)}>
                <NotifyBadge label={newerClientVersion} />
            </div>
        )
    }
}

const getMenuDisabledPermission = (entrie: ISelectedInfo, type: string) => {
    const helperModel: {
        [index: string]: string
    } = {
        key: 'digitalSignage',
        action: 'update',
    }

    switch (type) {
        case 'ds_date':
            helperModel.subaction = 'changeDateTime'
            return !helpers.isAvailableAction(entrie, helperModel)

        case 'notifications_settings':
            helperModel.subaction = 'changeNotificationSettings'
            return !helpers.isAvailableAction(entrie, helperModel)

        case 'edit':
            helperModel.subaction = 'generalEditing'
            return !helpers.isAvailableAction(entrie, helperModel)

        case 'settings':
            helperModel.subaction = 'changeSettings'
            return !helpers.isAvailableAction(entrie, helperModel)

        default:
            return false
    }
}

let volumeTimeout: ReturnType<typeof setTimeout> | null = null

export const DeviceVolume = ({ device }: { device: { volume: number; type: string; id: number } }) => {
    const [volumeValue, setVolumeValue] = useState(() => device.volume || 0)

    useEffect(() => {
        if (!volumeTimeout) {
            setVolumeValue(device.volume)
        }
    }, [device.volume])

    const changeVolume = (value: number) => {
        if (volumeTimeout) {
            clearTimeout(volumeTimeout)
        }

        setVolumeValue(value)
        volumeTimeout = setTimeout(() => {
            if (volumeTimeout) {
                clearTimeout(volumeTimeout)
            }
            api.send('changeVolume', { [`${device.type}Id`]: device.id, volume: value }, { hideLoader: true })
        }, 600)
    }

    return (
        <>
            <RangeVolume volume={volumeValue} onChange={changeVolume} />
            <div className={styles.volumeDependencyInfo}>{translate('volumeDependencyInfo')}</div>
        </>
    )
}

interface IMenuItemOwnProps {
    selectedInfo?: ISelectedInfo
    afterAction?: () => void
    data: { name: string }
    selectedItems?: ISelectedItems[]
    onClick?: () => void
    dropdown?: { id: string; text: string }
    beforeAction?: () => void
    rebootDeviceSelectedItems?: ISelectedItems[]
    forceDisable?: boolean
    className?: string
}

interface IMenuItemDispatchProps {
    openModal: (data: IDisplaysModal) => void
    enableIdentificationAction: (devices: IdentificationDevice[]) => void
    disableIdentificationAction: (devices: IdentificationDevice[]) => void
}

type IMenuItemComponentProps = IMenuItemOwnProps & IMenuItemDispatchProps

export interface ISelectedItems extends IdentificationDevice {
    online: boolean
    currentlyPlaying?: ISelectedInfo['currentlyPlaying']
}

type DeviceCommandTarget = {
    id?: number[]
    groupId?: number[]
}

type ToolbarAction = (selectedInfo?: ISelectedInfo, selectedItems?: ISelectedItems[]) => unknown

const MenuItemComponent = (p_: IMenuItemComponentProps) => {
    const [state, setState] = useState<{ active: string[] }>({
        active: [],
    })

    const { edit } = toolbarMethods()

    const afterAction = () => {
        if (p_.afterAction) {
            p_.afterAction()
        }
    }

    const isOnline = () => {
        if (p_.selectedInfo) {
            return getSelectInfoStatus(p_.selectedInfo) === 'online'
        } else if (p_.selectedItems && p_.selectedItems.length > 0) {
            return p_.selectedItems.some((item) => item.status === 'online')
        }
    }

    const isDisableInOfflineWithTooltip = () => {
        switch (p_.data.name) {
            case 'playback_state':
            case 'play_content':
            case 'downloadContent':
            case 'changeServer':
            case 'installedApplicationList':
            case 'android_permissions':
            case 'identification_on':
            case 'identification_off':
                return true
            default:
                return false
        }
    }

    const isEnableOfflineTooltip = () => {
        return !isOnline() && isDisableInOfflineWithTooltip()
    }

    const isDisableMenuItem = () => {
        return !isOnline() && isDisableInOfflineWithTooltip()
    }

    const getSelectInfoStatus = (selectedInfo: ISelectedInfo) => {
        return selectedInfo ? selectedInfo.status : null
    }

    const getDisplayActions = () => {
        const { openModal, enableIdentificationAction, disableIdentificationAction } = p_

        const actions: Record<string, ToolbarAction> = {
            remoteDeviceControl: () => openModal({ modalState: 'remoteDeviceControl', modalOptions: {} }),
            edit: (selectedInfo) => selectedInfo && edit(selectedInfo),
            information: () => openModal({ modalState: 'infoDisplay', modalOptions: {} }),
            setLicense: () => openModal({ modalState: 'setLicense', modalOptions: {} }),
            settings: () => openModal({ modalState: 'displaySettings', modalOptions: {} }),
            schedule: (selectedInfo) => {
                if (!selectedInfo) return

                changeLocation({
                    pathname: `/${routes.schedule.path}`,
                    search: getURLSearchParamsString({
                        scheduleData: configureScheduleLocations(selectedInfo),
                    }),
                })
            },
            reboot_device: (selectedInfo, selectedItems = []) => {
                const { id, groupId } = getDigitalSignageAndGroupIds(selectedItems)
                const data: DeviceCommandTarget = {}
                if (groupId && groupId.length) {
                    data.groupId = groupId
                }
                if (id && id.length) {
                    data.id = id
                }

                api.send('rebootDisplay', data)
            },
            restart_application: (selectedInfo, selectedItems = []) => {
                const { id, groupId } = getDigitalSignageAndGroupIds(selectedItems)
                const data: DeviceCommandTarget = {}
                if (groupId && groupId.length) {
                    data.groupId = groupId
                }
                if (id && id.length) {
                    data.id = id
                }
                api.send('digitalSignageSendRestartApp', data)
            },
            switchOff: (selectedInfo, selectedItems = []) => {
                const id = selectedItems.map((selectedItem) => selectedItem.id)

                api.send('shutdownDisplay', { id })
            },
            dsPingTest: (selectedInfo, selectedItems = []) => {
                const digitalSignageIds = selectedItems
                    .filter((item) => !item.online && item.type === 'digitalSignage')
                    .map((item) => item.id)

                if (digitalSignageIds.length) {
                    api.send('dsPingTest', { digitalSignageIds })
                }
            },
            screenshot: (selectedInfo, selectedItems = []) => {
                const { id, groupId } = getDigitalSignageAndGroupIds(selectedItems)

                id.length && api.send('updateScreenshotCb', { id, minified: true })
                groupId.length && api.send('updateScreenshotCb', { groupId, minified: true })
            },
            notifications_settings: () => openModal({ modalState: 'displayNotification', modalOptions: {} }),
            ds_date: () =>
                openModal({ modalState: 'dateTimeDisplay', modalOptions: { mod: 'clear', showCloseIcon: false } }),
            identification_on: (selectedInfo, selectedItems = []) => {
                return enableIdentificationAction(
                    [selectedInfo, ...selectedItems].filter((item): item is ISelectedInfo | ISelectedItems =>
                        isExist(item)
                    )
                )
            },
            identification_off: (selectedInfo, selectedItems = []) =>
                disableIdentificationAction(
                    [selectedInfo, ...selectedItems].filter((item): item is ISelectedInfo | ISelectedItems =>
                        isExist(item)
                    )
                ),
            android_permissions: () =>
                openModal({ modalState: 'permissions', modalOptions: { mod: 'clear', showCloseIcon: false } }),
            installedApplicationList: () => {
                openModal({
                    modalState: 'displayInstalledApplicationList',
                    modalOptions: { mod: 'clear', showCloseIcon: false },
                })
            },
            downloadContent: () => {
                openModal({
                    modalState: 'downloadContent',
                    modalOptions: { mod: 'clear', showCloseIcon: false },
                })
            },
            playback_state: () => {
                return openModal({
                    modalState: 'displayAudioControll',
                    modalOptions: {
                        mod: 'clear',
                        showCloseIcon: true,
                        closeIconSize: 30,
                        closeIconColor: 'white',
                    },
                })
            },
            play_content: () => {
                return openModal({
                    modalState: 'displayPlayAudioControll',
                    modalOptions: {
                        mod: 'clear',
                        showCloseIcon: true,
                        closeIconSize: 30,
                        closeIconColor: 'white',
                    },
                })
            },
            changeServer: () => openModal({ modalState: 'changeServer', modalOptions: {} }),
            uploadLogs: () =>
                openModal({ modalState: 'uploadLogs', modalOptions: { mod: 'clear', showCloseIcon: false } }),
        }

        return actions
    }

    const isEnableMenuItemErrorStatus = (items: { status: string }[]) => {
        let result = items && items.map((item) => item.status === 'error')
        return result && result.every((item) => item)
    }

    const isEnableMenuItemOnlineDisplayOff = (items: { status: string }[]) => {
        let result = items && items.map((item) => item.status === 'online_display_off')
        return result && result.every((item) => item)
    }

    const isEnableMenuItemErrorAndOnlineDisplayOffStatus = (items: { status: string }[]) => {
        let resultError = items && items.some((item) => item.status === 'error')
        let resultOnlineDisplayOff = items && items.some((item) => item.status === 'online_display_off')
        let resultOffline = items && items.some((item) => item.status === 'offline')
        return resultError && resultOnlineDisplayOff && !resultOffline
    }

    const isEnableMenuItemOnlineAndOnlineDisplayOffStatus = (items: { status: string }[]) => {
        let resultOnline = items && items.some((item) => item.status === 'online')
        let resultOnlineDisplayOff = items && items.some((item) => item.status === 'online_display_off')
        let resultOffline = items && items.some((item) => item.status === 'offline')
        return resultOnline && resultOnlineDisplayOff && !resultOffline
    }

    const isEnableMenuItemErrorAndOnlineStatus = (items: { status: string }[]) => {
        let resultError = items && items.some((item) => item.status === 'error')
        let resultOnline = items && items.some((item) => item.status === 'online')
        let resultOffline = items && items.some((item) => item.status === 'offline')
        return resultError && resultOnline && !resultOffline
    }

    const isEnableMenuItemErrorAndOnlineStatusAndOnlineDisplayOffStatus = (items: { status: string }[]) => {
        let resultError = items && items.some((item) => item.status === 'error')
        let resultOnline = items && items.some((item) => item.status === 'online')
        let resultOnlineDisplayOff = items && items.some((item) => item.status === 'online_display_off')
        let resultOffline = items && items.some((item) => item.status === 'offline')
        return resultError && resultOnlineDisplayOff && resultOnline && !resultOffline
    }

    const someStatusOffline = (items: { status: string }[]) => {
        return items && items.some((item) => item.status === 'offline')
    }

    const disabled = (p_.selectedItems && p_.selectedItems.length === 0) || isDisableMenuItem() || p_.forceDisable
    const onClickAction = getDisplayActions()[p_.data.name]
        ? () => {
              return getDisplayActions()[p_.data.name](p_.selectedInfo, p_.selectedItems)
          }
        : p_.onClick
        ? p_.onClick
        : () => {}
    const onClickActionRebootForErrorStatus = getDisplayActions()[p_.data.name]
        ? () => {
              return getDisplayActions()[p_.data.name](p_.selectedInfo, p_.rebootDeviceSelectedItems)
          }
        : p_.onClick
        ? p_.onClick
        : () => {}

    if (!p_.dropdown && p_.data.name !== 'reboot_device') {
        return (
            <Tooltip isEnabled={isEnableOfflineTooltip()} title={translate('availableInOnline')} position="left">
                <div
                    onClick={
                        !disabled
                            ? () => {
                                  if (p_.beforeAction) {
                                      p_.beforeAction()
                                  }
                                  onClickAction()
                                  afterAction()
                              }
                            : () => {}
                    }
                    className={cn(styles.menuItem, p_.className, {
                        [item.disabled]: disabled,
                        [styles.menuItemNormal]: p_.data.name === 'ds_date',
                    })}
                >
                    {translate(p_.data.name)}
                    {getClientVersion(p_.selectedInfo, p_.data.name)}
                </div>
            </Tooltip>
        )
    } else if (!p_.dropdown && p_.data.name === 'reboot_device') {
        return (
            <Tooltip isEnabled={isEnableOfflineTooltip()} title={translate('availableInOnline')} position="left">
                <div
                    onClick={
                        (p_.rebootDeviceSelectedItems && isEnableMenuItemErrorStatus(p_.rebootDeviceSelectedItems)) ||
                        (p_.rebootDeviceSelectedItems &&
                            isEnableMenuItemErrorAndOnlineStatusAndOnlineDisplayOffStatus(
                                p_.rebootDeviceSelectedItems
                            )) ||
                        (p_.rebootDeviceSelectedItems &&
                            isEnableMenuItemOnlineDisplayOff(p_.rebootDeviceSelectedItems)) ||
                        (p_.rebootDeviceSelectedItems &&
                            isEnableMenuItemErrorAndOnlineStatus(p_.rebootDeviceSelectedItems)) ||
                        (p_.rebootDeviceSelectedItems &&
                            isEnableMenuItemErrorAndOnlineDisplayOffStatus(p_.rebootDeviceSelectedItems)) ||
                        (p_.rebootDeviceSelectedItems &&
                            isEnableMenuItemOnlineAndOnlineDisplayOffStatus(p_.rebootDeviceSelectedItems))
                            ? () => {
                                  if (p_.beforeAction) {
                                      p_.beforeAction()
                                  }
                                  onClickActionRebootForErrorStatus()
                                  afterAction()
                              }
                            : !disabled
                            ? () => {
                                  if (p_.beforeAction) {
                                      p_.beforeAction()
                                  }
                                  onClickAction()
                                  afterAction()
                              }
                            : () => {}
                    }
                    className={cn(styles.menuItem, {
                        [item.disabled]:
                            p_.rebootDeviceSelectedItems && someStatusOffline(p_.rebootDeviceSelectedItems)
                                ? true
                                : p_.rebootDeviceSelectedItems &&
                                  isEnableMenuItemErrorStatus(p_.rebootDeviceSelectedItems)
                                ? false
                                : p_.rebootDeviceSelectedItems &&
                                  isEnableMenuItemOnlineDisplayOff(p_.rebootDeviceSelectedItems)
                                ? false
                                : p_.rebootDeviceSelectedItems &&
                                  isEnableMenuItemErrorAndOnlineDisplayOffStatus(p_.rebootDeviceSelectedItems)
                                ? false
                                : disabled,
                    })}
                >
                    {translate(p_.data.name)}
                    {getClientVersion(p_.selectedInfo, p_.data.name)}
                </div>
            </Tooltip>
        )
    }

    return (
        <DropMenu
            active={state.active}
            onChange={(active) => {
                setState((prevState) => ({ ...prevState, active }))
            }}
            mod="centered"
            buttonWrapperClassName={cn(grid.fullWidth)}
            items={[
                {
                    id: p_.dropdown?.id ?? p_.data.name,
                    disabled,
                    button: (
                        <div
                            className={cn(styles.menuItem, grid.fullWidth, {
                                [item.disabled]: disabled,
                            })}
                        >
                            {translate(p_.data.name)}
                        </div>
                    ),
                    children: (
                        <div className={styles.deleteGroup}>
                            <div className={cn(grid.p_mini, textStyles.center)}>{p_.dropdown && p_.dropdown.text}</div>
                            <div className={cn(grid.mb_mini, grid.row, grid.center)}>
                                <Button
                                    mod="fill"
                                    indentSize="mini"
                                    className={cn(grid.mr_mini)}
                                    onClick={() => {
                                        if (!disabled) {
                                            onClickAction()
                                            afterAction()
                                            setState((prevState) => ({ ...prevState, active: [] }))
                                        }
                                    }}
                                >
                                    {translate('yes')}
                                </Button>
                                <Button
                                    indentSize="mini"
                                    onClick={() => setState((prevState) => ({ ...prevState, active: [] }))}
                                >
                                    {translate('no')}
                                </Button>
                            </div>
                        </div>
                    ),
                },
            ]}
        />
    )
}

const mapDispatchToProps = {
    openModal: displaysActions.openModal,
    enableIdentificationAction: enableIdentification,
    disableIdentificationAction: disableIdentification,
}

export const MenuItem = connect(null, mapDispatchToProps)(MenuItemComponent)

const DisplaysToolbarContent = (p_: IDisplaysToolbarContentProps) => {
    const {
        state,
        setState,
        enterSleepMode,
        enableRefreshAnimation,
        exitSleepMode,
        removeGroup,
        toggleRemoveAll,
    } = DisplaysToolbarContentMethods(p_)

    const { editWithoutAddress, editGroup } = toolbarMethods()

    const renderMenuItemList = () => {
        const isOnline = p_.selectedInfo.online
        const isIdentificationImplemented = p_.selectedInfo.identificationImplementedQ
        const platform = p_.selectedInfo.platform

        return p_.selectedInfo.menu.map((menuItem, index) => {
            if (!isOnline && ['ds_date', 'android_permissions'].includes(menuItem.name)) {
                return null
            }

            if (!isIdentificationImplemented && ['identification_on', 'identification_off'].includes(menuItem.name)) {
                return null
            }

            if (menuItem.name === 'schedule' && !helpers.isAvailable({ key: 'schedule', action: 'read' })) {
                return null
            }

            if (menuItem.name === 'settings' && !platform) {
                return null
            }

            if (getMenuDisabledPermission(p_.selectedInfo, menuItem.name)) {
                return null
            }

            return (
                <MenuItem
                    key={`menuItem_${index}`}
                    data={menuItem}
                    selectedInfo={p_.selectedInfo}
                    className={'test-device-sidebar'}
                />
            )
        })
    }

    const checkPowerButtonDisableStatus = (selected: ISelectedInfo) => {
        const noCommands = displaysHelpers.getAvailableCommands(selected).length === 0
        const noWakeOnLan = !displaysHelpers.availableWakeOnLan(selected.platform)
        const noSleepMode = !isSleepStatus(selected)
        const isOffline = isOfflineStatus(selected)

        if (noCommands) {
            return true
        }

        return noWakeOnLan && noSleepMode && isOffline
    }

    const getSelectInfoStatus = (selectedInfo: ISelectedInfo) => {
        return selectedInfo ? selectedInfo.status : null
    }

    const isOfflineStatus = (selectedInfo: ISelectedInfo) => {
        return getSelectInfoStatus(selectedInfo) === 'offline'
    }

    const isSleepStatus = (selectedInfo: ISelectedInfo) => {
        return getSelectInfoStatus(selectedInfo) === 'sleep'
    }

    const isOnlineStatus = (selectedInfo: ISelectedInfo) => {
        return getSelectInfoStatus(selectedInfo) === 'online'
    }

    const isErrorStatus = (selectedInfo: ISelectedInfo) => {
        return getSelectInfoStatus(selectedInfo) === 'error'
    }

    const getGroupActions = () => {
        const { openModal, disableIdentificationAction, enableIdentificationAction } = p_
        const actions: Record<string, ToolbarAction> = {
            switchIpTvChannel: () =>
                openModal({
                    modalState: 'switchIpTvChannel',
                    modalOptions: { mod: 'clear', showCloseIcon: false },
                }),
            edit: (selectedInfo) => selectedInfo && editGroup(selectedInfo),
            information: () => openModal({ modalState: 'infoDisplay', modalOptions: {} }),
            settings: () => openModal({ modalState: 'displaySettings', modalOptions: {} }),
            schedule: (selectedInfo) => {
                if (!selectedInfo) return

                changeLocation({
                    pathname: `/${routes.schedule.path}`,
                    search: getURLSearchParamsString({
                        scheduleData: configureScheduleLocations(selectedInfo, 'groups'),
                    }),
                })
            },
            notifications_settings: () => openModal({ modalState: 'displayNotification', modalOptions: {} }),
            ds_date: () =>
                openModal({ modalState: 'dateTimeDisplay', modalOptions: { mod: 'clear', showCloseIcon: false } }),
            identification_on: (selectedInfo, selectedItems = []) => {
                return enableIdentificationAction(
                    [selectedInfo, ...selectedItems].filter((item): item is ISelectedInfo | ISelectedItems =>
                        isExist(item)
                    )
                )
            },
            identification_off: (selectedInfo, selectedItems = []) => {
                disableIdentificationAction(
                    [selectedInfo, ...selectedItems].filter((item): item is ISelectedInfo | ISelectedItems =>
                        isExist(item)
                    )
                )
            },
            synchronizationScheme: () => openModal({ modalState: 'displaysSynchronization', modalOptions: {} }),

            p2pDownloads: () =>
                openModal({ modalState: 'p2pDownloads', modalOptions: { mod: 'clear', showCloseIcon: true } }),
            setGroupTags: () => openModal({ modalState: 'setGroupTags', modalOptions: {} }),
            uploadLogs: () =>
                openModal({ modalState: 'uploadLogs', modalOptions: { mod: 'clear', showCloseIcon: false } }),
        }

        return actions
    }

    const isAvailableVolumeAction = (selectedInfo: ISelectedInfo) => {
        if (!selectedInfo || !selectedInfo.manufacturer) return true

        return selectedInfo.manufacturer !== 'BRIGHTSIGN'
    }

    const licenseIsActive = (selectedInfo: ISelectedInfo) => {
        if (!selectedInfo) return false

        return !!selectedInfo.activeQ
    }

    const displayOnOffSupport = (selectedInfo: ISelectedInfo) => {
        if (selectedInfo && selectedInfo.settings.comPorts) {
            return (
                selectedInfo.online &&
                selectedInfo.settings.comPorts.some((port: { Action: string }) => port.Action === 'PhilipsSICP')
            )
        }
        return false
    }

    const ipTvIsAvailable = (device: ISelectedInfo) => {
        if (!device) return false

        return !!(
            device.settings &&
            device.settings.enableIPTV &&
            isOnlineStatus(device) &&
            helpers.isAvailable({
                key: 'ipTv',
                action: 'update',
            })
        )
    }

    const isDisplayOff = p_.selectedInfo && p_.selectedInfo.status === 'online_display_off'
    const availableCommands = displaysHelpers.getAvailableCommands(p_.selectedInfo)
    const queryParameters = helpers.getQuery(p_.query, 'displays')
    const iptvChannelName = displaysHelpers.getChannelName(p_.selectedInfo && p_.selectedInfo.currentlyPlaying)

    return (
        <div className={styles.displayInfo}>
            {p_.selectedInfo && p_.selectedInfo.type !== 'group' && (
                <div>
                    {p_.selectedInfo.errorCode && isErrorStatus(p_.selectedInfo) && (
                        <Typography
                            type="title"
                            className={cn(textStyles.wordWrap, item.pointer, styles.errorText)}
                            onClick={() => {
                                setState((prevState) => ({
                                    ...prevState,
                                    descriptionModal: {
                                        open: true,
                                        text: p_.selectedInfo.errorCode.toString(),
                                    },
                                }))
                            }}
                        >
                            <Dotdotdot clamp={2}>{p_.selectedInfo.errorCode}</Dotdotdot>
                        </Typography>
                    )}
                    {p_.selectedInfo.name && (
                        <div className={grid.row}>
                            <Typography type="title" className={cn(styles.name, item.ellipsisWrapper)}>
                                <div className={item.ellipsis}>{p_.selectedInfo.name}</div>
                            </Typography>
                        </div>
                    )}
                    {p_.selectedInfo.description && (
                        <Typography
                            type="title"
                            className={cn(styles.name, textStyles.wordWrap, item.pointer)}
                            onClick={() => {
                                setState((prevState) => ({
                                    ...prevState,
                                    descriptionModal: {
                                        open: true,
                                        text: p_.selectedInfo.description,
                                    },
                                }))
                            }}
                        >
                            <Dotdotdot clamp={2}>{p_.selectedInfo.description}</Dotdotdot>
                        </Typography>
                    )}

                    <div className={cn(item.rel, item.viewInfoNoHiddenOverflow)}>
                        <div className={`${styles.info} ${styles[getSelectInfoStatus(p_.selectedInfo) || '']}`}>
                            {licenseIsActive(p_.selectedInfo) &&
                            helpers.isAvailableAction(null, {
                                key: 'digitalSignage',
                                action: 'update',
                                subaction: 'viewScreenshotHistory',
                            }) ? (
                                <Icon
                                    type="fullscreen"
                                    tooltip={{
                                        title: translate('showScreen'),
                                    }}
                                    size={18}
                                    color="white"
                                    onClick={() =>
                                        setState((prevState) => ({
                                            ...prevState,
                                            modalState: true,
                                        }))
                                    }
                                />
                            ) : (
                                <div />
                            )}
                            <div className={styles.lastUpdateDate}>
                                {helpers.getFormattedLocalDate(p_.selectedInfo.lastScreenshotDate, 'DD/MM/YYYY HH:mm')}
                            </div>

                            {licenseIsActive(p_.selectedInfo) &&
                            helpers.isAvailableAction(p_.selectedInfo, {
                                key: 'digitalSignage',
                                action: 'update',
                                subaction: 'updateScreenshot',
                            }) ? (
                                <Icon
                                    type="update"
                                    style={{
                                        animation: state.isUpdatingScreenShot
                                            ? 'loaderSpinAnimation 0.8s linear infinite'
                                            : '',
                                    }}
                                    size={18}
                                    color="white"
                                    tooltip={{
                                        title: translate('updateScreen'),
                                    }}
                                    disabled={isOfflineStatus(p_.selectedInfo) || isSleepStatus(p_.selectedInfo)}
                                    onClick={() => {
                                        enableRefreshAnimation()
                                        api.send('updateScreenshotCb', {
                                            id: p_.selectedInfo.id,
                                            minified: true,
                                        })
                                    }}
                                />
                            ) : (
                                <div />
                            )}
                        </div>
                        <Rectangle contain width={16} height={9} src={p_.selectedInfo.cover[0]}>
                            {iptvChannelName && (
                                <div className={styles.iptvNowPlaying}>
                                    <DotTooltip
                                        tooltip={{ html: translate('iptvNowPlaying') + ': ' + iptvChannelName }}
                                    >
                                        <div className={item.ellipsis}>
                                            {translate('iptvNowPlaying') + ': ' + iptvChannelName}
                                        </div>
                                    </DotTooltip>
                                </div>
                            )}
                            {!licenseIsActive(p_.selectedInfo) && (
                                <div className={styles.blocked}>
                                    <img
                                        className={styles.blockedImg}
                                        src={'images/displays/license_is_blocked.svg'}
                                        alt={'license is blocked'}
                                    />
                                </div>
                            )}
                        </Rectangle>
                        <DropMenu
                            active={state.active}
                            onChange={(active) => {
                                if (active.includes('iptv')) {
                                    p_.openModal({
                                        modalState: 'switchIpTvChannel',
                                        modalOptions: {},
                                    })
                                }

                                setState((prevState) => ({
                                    ...prevState,
                                    active,
                                }))
                            }}
                            mod="centered"
                            className={cn(styles[getSelectInfoStatus(p_.selectedInfo) || ''])}
                            items={[
                                {
                                    id: 'on_off_display',
                                    show:
                                        helpers.isAvailableAction(p_.selectedInfo, {
                                            key: 'digitalSignage',
                                            action: 'update',
                                        }) && displayOnOffSupport(p_.selectedInfo),
                                    icon: isDisplayOff ? 'display_on' : 'display_off',
                                    size: 24,
                                    tooltip: {
                                        title: translate(isDisplayOff ? 'display_on' : 'display_off'),
                                    },
                                    children: (
                                        <div className={cn(grid.colCenter, grid.p_md)}>
                                            <div className={cn(grid.w100, grid.mb_md, textStyles.center)}>
                                                <p className={cn(grid.w100, grid.mb_micro)}>
                                                    {translate(
                                                        isDisplayOff ? 'display_on_warning' : 'display_off_warning'
                                                    )}
                                                </p>
                                            </div>
                                            <div className={cn(grid.row, grid.justify, grid.mb_mini)}>
                                                <Button
                                                    mod="fill"
                                                    indentSize="mini"
                                                    className={cn(grid.mr_mini)}
                                                    onClick={() => {
                                                        api.send(isDisplayOff ? 'displayOn' : 'displayOff', {
                                                            digitalSignageId: [p_.selectedInfo.id],
                                                        })
                                                        setState((prevState) => ({
                                                            ...prevState,
                                                            active: [],
                                                        }))
                                                    }}
                                                >
                                                    {translate('yes')}
                                                </Button>
                                                <Button
                                                    mod={'withBorder'}
                                                    indentSize="mini"
                                                    onClick={() =>
                                                        setState((prevState) => ({
                                                            ...prevState,
                                                            active: [],
                                                        }))
                                                    }
                                                >
                                                    {translate('no')}
                                                </Button>
                                            </div>
                                        </div>
                                    ),
                                },
                                {
                                    id: 'power',
                                    show:
                                        licenseIsActive(p_.selectedInfo) &&
                                        helpers.isAvailableAction(p_.selectedInfo, {
                                            key: 'digitalSignage',
                                            action: 'update',
                                            subaction: 'shutdown',
                                        }),
                                    icon: 'power',
                                    tooltip: {
                                        title: translate('power'),
                                    },
                                    disabled: checkPowerButtonDisableStatus(p_.selectedInfo),
                                    children: (
                                        <div className={styles.deleteGroup}>
                                            {!isOfflineStatus(p_.selectedInfo) ? (
                                                <>
                                                    {(isOnlineStatus(p_.selectedInfo) ||
                                                        isErrorStatus(p_.selectedInfo)) &&
                                                    availableCommands.includes('shutdown') ? (
                                                        <>
                                                            <div
                                                                className={styles.menuItem}
                                                                onClick={() => {
                                                                    setState((prevState) => ({
                                                                        ...prevState,
                                                                        openPowerModal: 'id',
                                                                        active: [],
                                                                    }))
                                                                }}
                                                            >
                                                                {translate('switchOff')}
                                                            </div>
                                                            <div
                                                                className={styles.menuItem}
                                                                onClick={() => {
                                                                    setState((prevState) => ({
                                                                        ...prevState,
                                                                        sleepModeModal: 'digitalSignageId',
                                                                        active: [],
                                                                    }))
                                                                }}
                                                            >
                                                                {translate('sleep')}
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <>
                                                            {isSleepStatus(p_.selectedInfo) && (
                                                                <div
                                                                    className={styles.menuItem}
                                                                    onClick={() =>
                                                                        exitSleepMode({
                                                                            digitalSignageId: p_.selectedInfo.id,
                                                                        })
                                                                    }
                                                                >
                                                                    {translate('exitSleepMode')}
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                </>
                                            ) : (
                                                <>
                                                    {displaysHelpers.availableWakeOnLan(p_.selectedInfo.platform) && (
                                                        <div
                                                            className={styles.menuItem}
                                                            onClick={() => {
                                                                p_.openModal({
                                                                    modalState: 'wakeOnLan',
                                                                    modalOptions: {},
                                                                })
                                                            }}
                                                        >
                                                            {translate('enableDevice')}
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    ),
                                },
                                {
                                    id: 'restart',
                                    show:
                                        licenseIsActive(p_.selectedInfo) &&
                                        helpers.isAvailableAction(p_.selectedInfo, {
                                            key: 'digitalSignage',
                                            action: 'update',
                                            subaction: 'shutdown',
                                        }),
                                    icon: 'restart',
                                    size: 20,
                                    tooltip: {
                                        title: translate('restart'),
                                    },
                                    disabled: !isOnlineStatus(p_.selectedInfo) && !isErrorStatus(p_.selectedInfo),
                                    children: (
                                        <>
                                            {availableCommands.includes('reboot') && (
                                                <div
                                                    className={styles.menuItem}
                                                    onClick={() => {
                                                        api.send('rebootDisplay', {
                                                            id: p_.selectedInfo.id,
                                                        })
                                                        setState((prevState) => ({
                                                            ...prevState,
                                                            active: [],
                                                        }))
                                                    }}
                                                >
                                                    {translate('reboot_device')}
                                                </div>
                                            )}
                                            {availableCommands.includes('restartApp') && (
                                                <div
                                                    className={styles.menuItem}
                                                    onClick={() => {
                                                        api.send('digitalSignageSendRestartApp', {
                                                            id: p_.selectedInfo.id,
                                                        })
                                                        setState((prevState) => ({
                                                            ...prevState,
                                                            active: [],
                                                        }))
                                                    }}
                                                >
                                                    {translate('restart_application')}
                                                </div>
                                            )}
                                        </>
                                    ),
                                },
                                {
                                    id: 'volume',
                                    show:
                                        licenseIsActive(p_.selectedInfo) &&
                                        helpers.isAvailableAction(p_.selectedInfo, {
                                            key: 'digitalSignage',
                                            action: 'update',
                                            subaction: 'changeVolume',
                                        }) &&
                                        isAvailableVolumeAction(p_.selectedInfo),
                                    icon: 'volume_min',
                                    disabled: isSleepStatus(p_.selectedInfo) || isOfflineStatus(p_.selectedInfo),
                                    children: <DeviceVolume device={p_.selectedInfo} />,
                                    tooltip: {
                                        title: translate('volume'),
                                    },
                                },
                                {
                                    id: 'iptv',
                                    show: ipTvIsAvailable(p_.selectedInfo),
                                    icon: 'iptvIconButton',
                                    size: 24,
                                    disabled: false,
                                    tooltip: {
                                        title: translate('switchIpTvChannel'),
                                    },
                                },
                                {
                                    id: 'delete',
                                    show: helpers.isAvailableAction(p_.selectedInfo, {
                                        key: 'digitalSignage',
                                        action: 'delete',
                                    }),
                                    icon: 'delete',
                                    tooltip: {
                                        title: translate('delete'),
                                    },
                                    children: (
                                        <div className={cn(grid.colCenter, grid.p_md)}>
                                            <div className={cn(grid.w100, grid.mb_md, textStyles.center)}>
                                                <p className={cn(grid.w100, grid.mb_micro)}>
                                                    {translate('displayDeleteWarning')}?
                                                </p>
                                                <p className={grid.w100}>{translate('displayDeleteError')}.</p>
                                            </div>
                                            <div className={cn(grid.row, grid.justify, grid.mb_mini)}>
                                                <Button
                                                    mod="fill"
                                                    indentSize="mini"
                                                    className={cn(grid.mr_mini)}
                                                    onClick={() => {
                                                        api.send('deleteDisplay', { id: p_.selectedInfo.id })

                                                        setState((prevState) => ({
                                                            ...prevState,
                                                            active: [],
                                                        }))
                                                    }}
                                                >
                                                    {translate('yes')}
                                                </Button>
                                                <Button
                                                    mod={'withBorder'}
                                                    indentSize="mini"
                                                    onClick={() =>
                                                        setState((prevState) => ({
                                                            ...prevState,
                                                            active: [],
                                                        }))
                                                    }
                                                >
                                                    {translate('no')}
                                                </Button>
                                            </div>
                                        </div>
                                    ),
                                },
                            ]}
                        />
                    </div>
                    <div className={styles.menu}>
                        {state.active && state.active.length > 0 && (
                            <div className={cn(grid.full, item.abs, item.backOpacity, item.disabled)} />
                        )}
                        {queryParameters.query && p_.selectedInfo.groupId !== p_.groupId && (
                            <MenuItem
                                data={{ name: 'location' }}
                                onClick={function () {
                                    changeLocation({
                                        pathname: `/${routes.displays.path}`,
                                        search: getURLSearchParamsString({
                                            parentId: p_.selectedInfo.groupId,
                                        }),
                                    })
                                }}
                                className={'test-device-sidebar'}
                            />
                        )}
                        {p_.selectedInfo.menu && renderMenuItemList()}
                    </div>
                    {p_.selectedInfo.tags && (
                        <div className={styles.tags}>
                            {p_.selectedInfo.tags.map((tag, index) => (
                                <div key={`display__tag_${index}`} className={styles.tagWrapper}>
                                    <Tag value={tag.name} className={'test-device-tag'} />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {p_.selectedInfo && p_.selectedInfo.type === 'group' && (
                <div>
                    {p_.selectedInfo.name && (
                        <div className={grid.row}>
                            <Typography type="title" className={cn(styles.name, item.ellipsisWrapper)}>
                                <div className={item.ellipsis}>{p_.selectedInfo.name}</div>
                            </Typography>
                        </div>
                    )}
                    {p_.selectedInfo.description && (
                        <Typography
                            type="title"
                            className={cn(styles.name, textStyles.wordWrap, item.pointer)}
                            onClick={() => {
                                setState((prevState) => ({
                                    ...prevState,
                                    descriptionModal: {
                                        open: true,
                                        text: p_.selectedInfo.description,
                                    },
                                }))
                            }}
                        >
                            <Dotdotdot clamp={10}>{p_.selectedInfo.description}</Dotdotdot>
                        </Typography>
                    )}
                    <div>
                        <div className={`${styles.info} ${styles[getSelectInfoStatus(p_.selectedInfo) || '']}`}>
                            <Icon
                                type="fullscreen"
                                tooltip={{
                                    title: translate('installation'),
                                }}
                                size={18}
                                color="white"
                                onClick={() =>
                                    setState((prevState) => ({
                                        ...prevState,
                                        modalState: true,
                                    }))
                                }
                            />
                            {helpers.isAvailableAction(p_.selectedInfo, {
                                key: 'digitalSignage',
                                action: 'update',
                                subaction: 'updateScreenshot',
                            }) && (
                                <Icon
                                    type="update"
                                    style={{
                                        animation: state.isUpdatingScreenShot
                                            ? 'loaderSpinAnimation 0.8s linear infinite'
                                            : '',
                                    }}
                                    size={18}
                                    tooltip={{
                                        title: translate('updateScreen'),
                                    }}
                                    color="white"
                                    onClick={() => {
                                        enableRefreshAnimation()
                                        api.send('updateScreenshotCb', {
                                            groupId: p_.selectedInfo.id,
                                            minified: true,
                                        })
                                    }}
                                />
                            )}
                        </div>
                        <Rectangle width={16} height={9} className={grid.row}>
                            {p_.selectedInfo.cover.map((cover, index) => (
                                <div key={`group__cover_${index}`} className={grid.w50}>
                                    <Rectangle width={16} height={9} src={cover} />
                                </div>
                            ))}
                        </Rectangle>
                        <DropMenu
                            active={state.active}
                            onChange={(active) =>
                                setState((prevState) => ({
                                    ...prevState,
                                    active,
                                }))
                            }
                            mod="centered"
                            className={cn(styles[getSelectInfoStatus(p_.selectedInfo) || ''])}
                            items={[
                                {
                                    id: 'power',
                                    icon: 'power',
                                    show: helpers.isAvailableAction(p_.selectedInfo, {
                                        key: 'digitalSignage',
                                        action: 'update',
                                        subaction: 'shutdown',
                                    }),
                                    tooltip: {
                                        title: translate('power'),
                                    },
                                    children: (
                                        <div className={styles.deleteGroup}>
                                            <div
                                                className={styles.menuItem}
                                                onClick={() => {
                                                    setState((prevState) => ({
                                                        ...prevState,
                                                        sleepModeModal: 'groupId',
                                                        active: [],
                                                    }))
                                                }}
                                            >
                                                {translate('sleep')}
                                            </div>
                                            <div
                                                className={styles.menuItem}
                                                onClick={() =>
                                                    exitSleepMode({
                                                        groupId: p_.selectedInfo.id,
                                                    })
                                                }
                                            >
                                                {translate('exitSleepMode')}
                                            </div>
                                            <div
                                                className={styles.menuItem}
                                                onClick={() => {
                                                    setState((prevState) => ({
                                                        ...prevState,
                                                        openPowerModal: 'groupId',
                                                        active: [],
                                                    }))
                                                }}
                                            >
                                                {translate('switchOff')}
                                            </div>
                                        </div>
                                    ),
                                },
                                {
                                    id: 'restart',
                                    show: helpers.isAvailableAction(p_.selectedInfo, {
                                        key: 'digitalSignage',
                                        action: 'update',
                                        subaction: 'shutdown',
                                    }),
                                    icon: 'restart',
                                    size: 22,
                                    tooltip: {
                                        title: translate('restart'),
                                    },
                                    children: (
                                        <div className={styles.deleteGroup}>
                                            <div
                                                className={styles.menuItem}
                                                onClick={() => {
                                                    api.send('rebootDisplay', { groupId: p_.selectedInfo.id })

                                                    setState((prevState) => ({
                                                        ...prevState,
                                                        active: [],
                                                    }))
                                                }}
                                            >
                                                {translate('reboot_device')}
                                            </div>
                                            <div
                                                className={styles.menuItem}
                                                onClick={() => {
                                                    api.send('digitalSignageSendRestartApp', {
                                                        groupId: p_.selectedInfo.id,
                                                    })

                                                    setState((prevState) => ({
                                                        ...prevState,
                                                        active: [],
                                                    }))
                                                }}
                                            >
                                                {translate('restart_application')}
                                            </div>
                                        </div>
                                    ),
                                },
                                {
                                    id: 'volume',
                                    show: helpers.isAvailableAction(p_.selectedInfo, {
                                        key: 'digitalSignage',
                                        action: 'update',
                                        subaction: 'changeVolume',
                                    }),
                                    tooltip: {
                                        title: translate('volume'),
                                    },
                                    icon: 'volume_min',
                                    children: <DeviceVolume device={p_.selectedInfo} />,
                                },
                                {
                                    id: 'delete',
                                    show: helpers.isAvailableAction(p_.selectedInfo, {
                                        key: 'digitalSignage',
                                        action: 'delete',
                                    }),
                                    icon: 'delete',
                                    tooltip: {
                                        title: translate('delete'),
                                    },
                                    children: (
                                        <div className={styles.deleteGroup}>
                                            <div
                                                onClick={() => removeGroup(p_.selectedInfo)}
                                                className={styles.removeGroupButton}
                                            >
                                                {translate('deleteGroup')}
                                            </div>
                                            <div className={styles.removeAll}>
                                                <Checkbox
                                                    label={translate('saveDisplays')}
                                                    onClick={() => toggleRemoveAll()}
                                                    checked={!state.removeAll}
                                                />
                                            </div>
                                        </div>
                                    ),
                                },
                            ]}
                        />
                    </div>
                    <div className={styles.menu}>
                        {state.active && state.active.length > 0 && (
                            <div className={cn(grid.full, item.abs, item.backOpacity, item.disabled)} />
                        )}
                        {queryParameters.query && p_.selectedInfo.parentId !== p_.parentId && (
                            <MenuItem
                                data={{ name: 'location' }}
                                onClick={function () {
                                    changeLocation({
                                        pathname: `/${routes.displays.path}`,
                                        search: getURLSearchParamsString({
                                            parentId: p_.selectedInfo.parentId,
                                        }),
                                    })
                                }}
                                className={'test-device-sidebar'}
                            />
                        )}
                        {p_.selectedInfo.menu &&
                            p_.selectedInfo.menu.map((menuItem, index) => {
                                if (getSelectInfoStatus(p_.selectedInfo) !== 'online' && menuItem.name === 'ds_date') {
                                    return null
                                }

                                if (
                                    menuItem.name === 'schedule' &&
                                    !helpers.isAvailable({ key: 'schedule', action: 'read' })
                                ) {
                                    return null
                                }

                                if (getMenuDisabledPermission(p_.selectedInfo, menuItem.name)) {
                                    return null
                                }

                                const label = menuItem.name === 'settings' ? 'groupSettings' : menuItem.name

                                return (
                                    <div
                                        key={`group__menuItem_${index}`}
                                        className={styles.menuItem}
                                        onClick={
                                            getGroupActions()[menuItem.name]
                                                ? () => getGroupActions()[menuItem.name](p_.selectedInfo)
                                                : () => {}
                                        }
                                    >
                                        {translate(label)}
                                    </div>
                                )
                            })}
                        {helpers.isAvailableAction(null, {
                            key: 'digitalSignage',
                            action: 'update',
                            subaction: 'downloadDeviceInfo',
                        }) && (
                            <a
                                href={p_.downloadLink}
                                target="_blank"
                                className={cn(grid.fullWidth, item.block, textStyles.noUnderline)}
                                rel="noopener noreferrer"
                            >
                                <MenuItem
                                    data={{ name: 'downloadDevices' }}
                                    selectedItems={[p_.selectedInfo]}
                                    className={'test-device-sidebar'}
                                />
                            </a>
                        )}
                    </div>
                </div>
            )}
            <DisplaysScreenShots
                open={state.modalState}
                selectedInfo={p_.selectedInfo}
                onClose={() =>
                    setState((prevState) => ({
                        ...prevState,
                        modalState: false,
                    }))
                }
            />
            {!p_.selectedInfo && p_.selectedItems.length === 0 && p_.withoutAddress && (
                <div>
                    <div
                        className={cn(textStyles.normalPlus, textStyles['blackLight'], textStyles.center, grid.p_mini)}
                    >
                        {translate('devicesWithoutAddress')}:
                    </div>
                    <Typography type="title" className={styles.name}>
                        {translate('devicesWithoutAddressDescription')}.
                    </Typography>
                    <div
                        className={cn(
                            grid.center,
                            grid.row,
                            grid.p_md,
                            textStyles['blackLight'],
                            textStyles.normalPlus
                        )}
                    >
                        {`${p_.withoutAddress.length} ${translate('devicesCounter')}:`}
                    </div>
                    <ul>
                        {(() =>
                            p_.withoutAddress.map((displayItem, index) => (
                                <li
                                    key={`withoutAddress_${index}`}
                                    className={cn(grid.p_micro, grid.row, grid.noWrap, item.pointer)}
                                    onClick={
                                        helpers.isAvailableAction(p_.selectedInfo, {
                                            key: 'digitalSignage',
                                            action: 'update',
                                        })
                                            ? () => editWithoutAddress(displayItem)
                                            : () => {}
                                    }
                                >
                                    <Icon type="files" className={cn(grid.mr_md, grid.ml_micro)} />
                                    <span className={cn(item.ellipsisWrapper, textStyles['blackLight'])}>
                                        <div className={item.ellipsis}>{displayItem.name}</div>
                                    </span>
                                </li>
                            )))()}
                    </ul>
                </div>
            )}

            {p_.selectedInfo && <div className={grid.space} />}
            {p_.selectedInfo && (
                <div className={cn(grid.p_micro, grid.rowCenter, styles.closeBtn)}>
                    <Button mod={'withBorder'} onClick={p_.onCloseInfo} indentSize={'mini'} rounded={'full_normal'}>
                        {translate('close')}
                    </Button>
                </div>
            )}

            <ModalAccept
                open={!!state.openPowerModal}
                onReject={() =>
                    setState((prevState) => ({
                        ...prevState,
                        openPowerModal: false,
                    }))
                }
                onAccept={() => {
                    if (!state.openPowerModal) return

                    const data = { [state.openPowerModal]: p_.selectedInfo.id }
                    api.send('shutdownDisplay', data)

                    setState((prevState) => ({
                        ...prevState,
                        openPowerModal: false,
                    }))
                }}
                onClose={() =>
                    setState((prevState) => ({
                        ...prevState,
                        openPowerModal: false,
                    }))
                }
                description={translate(state.openPowerModal === 'groupId' ? 'powerOffWarning_' : 'powerOffWarning')}
                rejectLabel={translate('cancel')}
                acceptLabel={translate('off')}
            />
            <ModalAccept
                open={!!state.sleepModeModal}
                onReject={() =>
                    setState((prevState) => ({
                        ...prevState,
                        sleepModeModal: '',
                    }))
                }
                onAccept={() => {
                    if (!state.sleepModeModal) return

                    const data = { [state.sleepModeModal]: p_.selectedInfo.id }
                    enterSleepMode(data, () =>
                        setState((prevState) => ({
                            ...prevState,
                            sleepModeModal: '',
                        }))
                    )
                }}
                onClose={() =>
                    setState((prevState) => ({
                        ...prevState,
                        sleepModeModal: '',
                    }))
                }
                description={translate(state.sleepModeModal === 'groupId' ? 'sleepModeWarning_' : 'sleepModeWarning')}
                rejectLabel={translate('cancel')}
                acceptLabel={translate('go')}
            />
            <ModalAccept
                open={state.descriptionModal.open}
                onClose={() => {
                    setState((prevState) => ({
                        ...prevState,
                        descriptionModal: {
                            open: false,
                            text: '',
                        },
                    }))
                }}
                description={state.descriptionModal.text}
            />
        </div>
    )
}

class DisplaysToolbarContentClickOutside extends React.Component<IDisplaysToolbarContentProps> {
    render() {
        return <DisplaysToolbarContent {...this.props} />
    }
}

export default connect(null, mapDispatchToProps)(enhanceWithClickOutside(DisplaysToolbarContentClickOutside))
